(() => {
  // This page settles its layout after load (hero pinning remeasures, images
  // and fonts stream in). The browser's automatic scroll restoration fires
  // before that settles, so a reload while scrolled restores the old position
  // and then snaps — usually to the top — once the layout shifts under it.
  // Opt out so a reload starts cleanly at the top instead.
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const INSTANCE_KEY = '__hpgStackablePageChrome';
  const HERO_SELECTOR = '.hpg-stackable-bands-hero';
  const INTRO_SELECTOR = '.hpg-system-intro';
  const STICKY_BUY_SELECTOR = '[data-hpg-sticky-buy-bar]';
  const HEADER_SELECTOR = '.section-header';
  const ANNOUNCEMENT_SELECTOR = '.announcement-bar-section';
  const STICKY_HEADER_SELECTOR = 'sticky-header';
  const MOBILE_QUERY = '(max-width: 749px)';
  const MOBILE_OBSTRUCTION_SELECTORS = [
    'iframe[src*="pop-convert"]',
    'iframe[src*="popconvert"]',
    'iframe[src*="preview_bar"]',
    'iframe[src*="theme_preview"]',
    '[id*="pop-convert" i]',
    '[class*="pop-convert" i]',
    '[id*="popconvert" i]',
    '[class*="popconvert" i]',
    '[id*="preview-bar" i]',
    '[class*="preview-bar" i]',
    '[id*="shopify-preview" i]',
    '[class*="shopify-preview" i]',
  ];

  const pinnedHeroClass = 'hpg-stackable-bands-hero--pinned';
  const headerClasses = [
    'shopify-section-header-sticky',
    'shopify-section-header-hidden',
    'animate',
    'scrolled-past-header',
  ];

  function initStackablePageChrome(options = {}) {
    const force = Boolean(options.force);
    const hero = document.querySelector(HERO_SELECTOR);
    const intro = document.querySelector(INTRO_SELECTOR);
    const stickyBuyBar = document.querySelector(STICKY_BUY_SELECTOR);
    const header = document.querySelector(HEADER_SELECTOR);
    const announcement = document.querySelector(ANNOUNCEMENT_SELECTOR);
    const main = document.querySelector('#MainContent');

    if (!hero || !intro || !header) return;

    const existingInstance = window[INSTANCE_KEY];
    if (existingInstance && !force) {
      existingInstance.update?.();
      return;
    }

    existingInstance?.destroy?.();

    const stickyHeader = document.querySelector(STICKY_HEADER_SELECTOR);
    const previousPreventHide = stickyHeader?.preventHide;
    const heroSection = hero.closest('.shopify-section') || hero.parentElement;
    const contentSections = Array.from(main?.children || []).filter(
      (section) => section !== heroSection
    );
    const previousStyles = new Map();
    const previousObstructionStyles = new Map();
    let rafId = 0;
    let heroPinned = false;
    let stickyBuyVisible = false;
    let obstructionObserver = null;
    let stableMobileViewportHeight = 0;
    let stableMobileViewportWidth = 0;

    const rememberStyles = (element, properties) => {
      if (!element || previousStyles.has(element)) return;

      previousStyles.set(
        element,
        properties.reduce((styles, property) => {
          styles[property] = element.style[property];
          return styles;
        }, {})
      );
    };

    rememberStyles(announcement, ['position', 'top', 'right', 'left', 'width', 'zIndex']);
    rememberStyles(header, ['position', 'top', 'right', 'left', 'width', 'zIndex']);
    rememberStyles(main, ['paddingTop']);
    contentSections.forEach((section) => rememberStyles(section, ['position', 'zIndex']));

    if (stickyHeader) {
      stickyHeader.preventHide = true;
    }

    const readPageTop = (element) => element.getBoundingClientRect().top + window.scrollY;

    const setStyles = (element, styles) => {
      if (!element) return;
      Object.assign(element.style, styles);
    };

    const px = (value) => `${Math.max(0, Math.round(value * 100) / 100)}px`;

    const readLayoutViewportHeight = () => window.innerHeight || document.documentElement.clientHeight;
    const readLayoutViewportWidth = () => window.innerWidth || document.documentElement.clientWidth;

    const readStableViewportHeight = () => {
      const viewportHeight = readLayoutViewportHeight();
      const viewportWidth = readLayoutViewportWidth();

      if (!window.matchMedia(MOBILE_QUERY).matches) {
        stableMobileViewportHeight = 0;
        stableMobileViewportWidth = 0;
        return viewportHeight;
      }

      const isAtTop = (window.scrollY || document.documentElement.scrollTop || 0) <= 1;
      const widthChanged = stableMobileViewportWidth !== viewportWidth;

      if (!stableMobileViewportHeight || isAtTop || widthChanged) {
        stableMobileViewportHeight = viewportHeight;
        stableMobileViewportWidth = viewportWidth;
      }

      return stableMobileViewportHeight;
    };

    const syncViewportVars = (chromeHeight, announcementHeight) => {
      const viewportHeight = readStableViewportHeight();
      document.documentElement.style.setProperty('--hpg-stackable-chrome-height', px(chromeHeight));
      document.documentElement.style.setProperty('--hpg-stackable-announcement-height', px(announcementHeight));
      document.documentElement.style.setProperty('--hpg-stackable-viewport-height', px(viewportHeight));
    };

    const rememberObstructionStyles = (element) => {
      if (!element || previousObstructionStyles.has(element)) return;

      previousObstructionStyles.set(element, {
        display: element.style.display,
        visibility: element.style.visibility,
        pointerEvents: element.style.pointerEvents,
      });
    };

    const hideMobileObstructions = () => {
      if (!window.matchMedia(MOBILE_QUERY).matches) return;

      MOBILE_OBSTRUCTION_SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          if (!(element instanceof HTMLElement)) return;
          rememberObstructionStyles(element);
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
        });
      });
    };

    const applyChrome = () => {
      const announcementHeight = announcement ? announcement.getBoundingClientRect().height : 0;
      const headerHeight = header.getBoundingClientRect().height;
      const chromeHeight = announcementHeight + headerHeight;
      const headerTop = Math.max(0, announcementHeight - 1);
      const contentTop = Math.max(0, chromeHeight - 1);
      syncViewportVars(contentTop, announcementHeight);

      setStyles(announcement, {
        position: 'fixed',
        top: '0px',
        right: '0px',
        left: '0px',
        width: '100%',
        zIndex: '2',
      });

      setStyles(header, {
        position: 'fixed',
        top: px(headerTop),
        right: '0px',
        left: '0px',
        width: '100%',
        zIndex: '2',
      });

      setStyles(main, {
        paddingTop: px(contentTop),
      });

      contentSections.forEach((section) => {
        setStyles(section, {
          position: 'relative',
          zIndex: '3',
        });
      });

      header.classList.remove(...headerClasses);
      hideMobileObstructions();

      return contentTop;
    };

    const setHeroPinned = (pinned, heroHeight, chromeHeight) => {
      if (heroSection) {
        heroSection.style.minHeight = `${Math.round(heroHeight)}px`;
      }

      if (pinned === heroPinned) return;
      heroPinned = pinned;

      if (pinned) {
        hero.classList.add(pinnedHeroClass);
        Object.assign(hero.style, {
          position: 'fixed',
          top: px(chromeHeight),
          right: '0px',
          left: '0px',
          width: '100%',
          zIndex: '0',
        });
      } else {
        hero.classList.remove(pinnedHeroClass);
        hero.style.position = '';
        hero.style.top = '';
        hero.style.right = '';
        hero.style.left = '';
        hero.style.width = '';
        hero.style.zIndex = '';
      }
    };

    const setStickyBuyVisible = (visible) => {
      if (!stickyBuyBar || visible === stickyBuyVisible) return;
      stickyBuyVisible = visible;
      stickyBuyBar.classList.toggle('is-visible', visible);
      stickyBuyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      stickyBuyBar.inert = !visible;
    };

    const update = () => {
      rafId = 0;

      header.classList.remove(...headerClasses);

      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const chromeHeight = applyChrome();
      const heroHeight = hero.getBoundingClientRect().height;
      const introTop = readPageTop(intro);
      const introTouchPoint = Math.max(0, introTop - chromeHeight);
      const stickyBuyDelay = window.matchMedia(MOBILE_QUERY).matches
        ? Math.min(readStableViewportHeight() * 0.22, 180)
        : 0;

      setHeroPinned(scrollY < introTouchPoint, heroHeight, chromeHeight);
      setStickyBuyVisible(scrollY >= introTouchPoint + stickyBuyDelay);
    };

    const requestUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(update);
    };

    const destroy = () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      window.visualViewport?.removeEventListener('resize', requestUpdate);

      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      if (stickyHeader) {
        stickyHeader.preventHide = previousPreventHide;
      }

      previousStyles.forEach((styles, element) => {
        Object.assign(element.style, styles);
      });

      hero.classList.remove(pinnedHeroClass);
      hero.style.position = '';
      hero.style.top = '';
      hero.style.right = '';
      hero.style.left = '';
      hero.style.width = '';
      hero.style.zIndex = '';

      setStickyBuyVisible(false);
      if (stickyBuyBar) {
        stickyBuyBar.inert = true;
      }

      if (heroSection) {
        heroSection.style.minHeight = '';
      }

      previousObstructionStyles.forEach((styles, element) => {
        Object.assign(element.style, styles);
      });

      if (obstructionObserver) {
        obstructionObserver.disconnect();
        obstructionObserver = null;
      }

      document.documentElement.classList.remove('hpg-stackable-bands-page');
      document.body?.classList.remove('hpg-stackable-bands-page');
      document.documentElement.style.removeProperty('--hpg-stackable-chrome-height');
      document.documentElement.style.removeProperty('--hpg-stackable-announcement-height');
      document.documentElement.style.removeProperty('--hpg-stackable-viewport-height');

      delete window[INSTANCE_KEY];
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.visualViewport?.addEventListener('resize', requestUpdate);
    if (stickyBuyBar) {
      stickyBuyBar.inert = true;
    }
    obstructionObserver = new MutationObserver(hideMobileObstructions);
    obstructionObserver.observe(document.documentElement, { childList: true, subtree: true });
    window[INSTANCE_KEY] = { destroy, update: requestUpdate };
    document.documentElement.classList.add('hpg-stackable-bands-page');
    document.body?.classList.add('hpg-stackable-bands-page');
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStackablePageChrome, { once: true });
  } else {
    initStackablePageChrome();
  }

  window.addEventListener('load', () => initStackablePageChrome(), { once: true });
  document.addEventListener('shopify:section:load', () => initStackablePageChrome({ force: true }));

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target?.nodeType === 1 && event.target.querySelector?.(HERO_SELECTOR)) {
        window[INSTANCE_KEY]?.destroy();
      }
    });
  }
})();
